import { ingestEvents } from '@/atoms/stats'
import { useSecondaryPage } from '@/PageManager'
import { getReplaceableCoordinateFromEvent, isReplaceableEvent } from '@/lib/event'
import { toProfile } from '@/lib/link'
import { isSmallScreenAtom } from '@/atoms/screenSize'
import { useAtomValue } from 'jotai'
import { loadRelayList } from '@nostr/gadgets/lists'
import client from '@/services/client.service'
import { TFeedSubRequest } from '@/types'
import { Emoji as TEmoji } from '@nostr/gadgets/lists'
import { Filter } from '@nostr/tools/filter'
import * as kinds from '@nostr/tools/kinds'
import { Event } from '@nostr/tools/wasm'
import { HugeiconsIcon } from '@hugeicons/react'
import { SquareArrowReloadIcon } from '@hugeicons/core-free-icons'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Emoji from './Emoji'
import { FormattedTimestamp } from './FormattedTimestamp'
import Nip05 from './Nip05'
import UserAvatar from './UserAvatar'
import Username from './Username'
import { tagNameEquals } from '@/lib/tag'

const SHOW_COUNT = 50
const LIMIT = 500

type TReactionItem =
  | {
      type: 'reaction'
      id: string
      pubkey: string
      created_at: number
      emoji: TEmoji | string
    }
  | {
      type: 'repost'
      id: string
      pubkey: string
      created_at: number
    }

export default function ReactionList({
  event,
  selectedRelays
}: {
  event: Event
  selectedRelays?: string[]
}) {
  const { t } = useTranslation()
  const { push } = useSecondaryPage()
  const isSmallScreen = useAtomValue(isSmallScreenAtom)
  const [subRequests, setSubRequests] = useState<null | TFeedSubRequest[]>(null)
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const [showCount, setShowCount] = useState(SHOW_COUNT)
  const bottomRef = useRef<HTMLDivElement | null>(null)

  const reactionItems = useMemo(() => {
    const items: TReactionItem[] = []

    const sortedEvents = events.sort((a, b) => b.created_at - a.created_at)
    for (let i = 0; i < sortedEvents.length; i++) {
      const evt = sortedEvents[i]

      switch (evt.kind) {
        case kinds.Reaction: {
          items.push({
            type: 'reaction',
            id: evt.id,
            pubkey: evt.pubkey,
            created_at: evt.created_at,
            emoji: parseReactionEmoji(evt)
          })
          break
        }

        case kinds.Repost:
        case kinds.GenericRepost: {
          items.push({
            type: 'repost',
            id: evt.id,
            pubkey: evt.pubkey,
            created_at: evt.created_at
          })

          break
        }
      }
    }

    return items
  }, [events])

  useEffect(() => {
    if (!bottomRef.current) return
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShowCount((c) => (c < reactionItems.length ? c + SHOW_COUNT : c))
        }
      },
      { rootMargin: '10px', threshold: 0.1 }
    )
    obs.observe(bottomRef.current)
    return () => obs.disconnect()
  }, [reactionItems.length])

  useEffect(() => {
    setShowCount(SHOW_COUNT)
  }, [event.id, selectedRelays])

  useEffect(() => {
    let isCancelled = false
    ;(async () => {
      const replaceableCoordinate = isReplaceableEvent(event.kind)
        ? getReplaceableCoordinateFromEvent(event)
        : undefined

      const filters: Filter[] = [
        {
          '#e': [event.id],
          kinds: [kinds.Reaction, kinds.Repost, kinds.GenericRepost],
          limit: LIMIT
        }
      ]

      if (replaceableCoordinate) {
        filters.push({
          '#a': [replaceableCoordinate],
          kinds: [kinds.Reaction, kinds.Repost, kinds.GenericRepost],
          limit: LIMIT
        })
      }

      const relayUrls = selectedRelays
        ? selectedRelays
        : (await loadRelayList(event.pubkey)).items
            .filter((r) => r.read)
            .map((r) => r.url)
            .concat(window.hallway.universe.bigRelayUrls)

      if (isCancelled) return
      setSubRequests(
        filters.map((filter) => ({
          source: 'relays',
          urls: relayUrls,
          filter
        }))
      )
    })()

    return () => {
      isCancelled = true
    }
  }, [event.id, event.kind, event.pubkey, selectedRelays])

  useEffect(() => {
    if (!subRequests || subRequests.length === 0) return

    setLoading(true)
    setEvents([])

    const subc = client.subscribeTimeline(
      subRequests,
      {},
      {
        onEvents: (events, isFinal) => {
          if (isFinal) setLoading(false)
          if (events.length > 0) {
            setEvents(events)
            ingestEvents(events)
          } else if (isFinal) {
            setEvents([])
          }
        },
        onNew: (evt) => {
          ingestEvents([evt])
          setEvents((oldEvents) => {
            if (oldEvents.some((existing) => existing.id === evt.id)) return oldEvents
            return [evt, ...oldEvents].sort((a, b) => b.created_at - a.created_at)
          })
        }
      }
    )

    return () => subc.close()
  }, [subRequests])

  return (
    <div className="min-h-[80vh]">
      {reactionItems.slice(0, showCount).map((item) => {
        if (item.type === 'reaction') {
          return (
            <div
              key={item.id}
              title={item.id}
              className="px-4 py-3 border-b transition-colors cursor-pointer hover:bg-muted/30 active:bg-muted/30 flex items-center gap-3"
              onClick={() => push(toProfile(item.pubkey))}
            >
              <div className="w-6 flex flex-col items-center">
                <Emoji
                  emoji={item.emoji}
                  classNames={{
                    text: 'text-xl'
                  }}
                />
              </div>

              <UserAvatar user={item.pubkey} size="medium" className="shrink-0" />

              <div className="flex-1 w-0">
                <Username
                  user={item.pubkey}
                  className="text-sm font-semibold text-muted-foreground hover:text-foreground max-w-fit truncate"
                  skeletonClassName="h-3"
                />
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Nip05 user={item.pubkey} append="·" />
                  <FormattedTimestamp
                    timestamp={item.created_at}
                    className="shrink-0"
                    short={isSmallScreen}
                  />
                </div>
              </div>
            </div>
          )
        }

        if (item.type === 'repost') {
          return (
            <div
              key={item.id}
              className="px-4 py-3 border-b transition-colors cursor-pointer hover:bg-muted/30 active:bg-muted/30 flex items-center gap-3"
              onClick={() => push(toProfile(item.pubkey))}
            >
              <HugeiconsIcon icon={SquareArrowReloadIcon} className="text-green-400 size-5" />

              <UserAvatar user={item.pubkey} size="medium" className="shrink-0" />

              <div className="flex-1 w-0">
                <Username
                  user={item.pubkey}
                  className="text-sm font-semibold text-muted-foreground hover:text-foreground max-w-fit truncate"
                  skeletonClassName="h-3"
                />
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Nip05 user={item.pubkey} append="·" />
                  <FormattedTimestamp
                    timestamp={item.created_at}
                    className="shrink-0"
                    short={isSmallScreen}
                  />
                </div>
              </div>
            </div>
          )
        }

        return null
      })}

      <div ref={bottomRef} />

      <div className="text-sm mt-2 text-center text-muted-foreground">
        {loading
          ? t('Loading')
          : reactionItems.length > 0
            ? t('No more reactions')
            : t('No reactions yet')}
      </div>
    </div>
  )
}

// Per NIP-25: content can be '+', '-', '' (treated as '+'), a unicode emoji,
// or ':shortcode:' referencing an emoji tag.
export function parseReactionEmoji(evt: Event): TEmoji | string {
  const content = evt.content
  if (!content || content === '+') return '+'

  if (evt.content.startsWith(':') && evt.content.endsWith(':')) {
    const shortcode = evt.content.slice(1, -1)
    const emojiTag = evt.tags.find(
      (tag) => tagNameEquals('emoji')(tag) && tag[1] === shortcode && !!tag[2]
    )
    if (emojiTag) {
      return { shortcode, url: emojiTag[2] }
    }
  }

  return content
}
